import {
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
  signal,
  Signal,
} from "@angular/core";
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
  MatOption,
} from "@angular/material/autocomplete";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import {
  GetOptionsByIdInteractor,
  OptionsByIdComponent,
} from "../../../../features/selector-options/options-by-id.providers";
import {
  GetSelectorOptionsInteractor,
  SelectorOptionsComponent,
} from "../../../../features/selector-options/selector-options.providers";
import { SharedPipesModule } from "../../../pipes/shared-pipes.module";
import { AutocompleteSelectorComponent } from "../autocomplete-selector/autocomplete-selector.component";
import { FilterOutput, FilterType } from "../filter.interfaces";
import { SelectedChipComponent } from "../selected-chip/selected-chip.component";
import { SelectorOptionsService } from "../selector-options.service";
import { TimePeriodSelectorComponent } from "../time-period-selector/time-period-selector.component";

interface UrlFilterOption {
  readonly value: string;
  readonly type: FilterType;
}

@Component({
  standalone: true,
  selector: "app-aggregated-filter",
  templateUrl: "./aggregated-filter.component.html",
  styleUrl: "./aggregated-filter.component.scss",
  imports: [
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatProgressSpinner,
    MatLabel,
    MatFormField,
    MatInput,
    AutocompleteSelectorComponent,
    MatIcon,
    SelectedChipComponent,
    SharedPipesModule,
    TranslateModule,
    TimePeriodSelectorComponent,
  ],
})
export class AggregatedFilterComponent implements OnInit {
  public readonly filterTypes = input.required<FilterType[]>();
  public readonly fixedFilters = input<FilterOutput[]>([]);
  @Output() public readonly filtersChanged = new EventEmitter<FilterOutput[]>();
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  protected readonly FilterType = FilterType;
  protected readonly filterInteractors: Signal<GetSelectorOptionsInteractor[]> =
    ((selectorOptionsService: SelectorOptionsService) =>
      computed(() =>
        this.filterTypes().map((filterType) =>
          SelectorOptionsComponent.provideGetOptionsInteractorByType(
            filterType,
            selectorOptionsService,
          ),
        ),
      ))(inject(SelectorOptionsService));

  protected readonly filterInteractorsByIds: Signal<
    GetOptionsByIdInteractor[]
  > = ((selectorOptionsService: SelectorOptionsService) =>
    computed(() =>
      this.filterTypes().map((filterType) =>
        OptionsByIdComponent.provideGetOptionsInteractorByType(
          filterType,
          selectorOptionsService,
        ),
      ),
    ))(inject(SelectorOptionsService));

  protected readonly areFiltersOpen = signal(false);
  protected readonly activeFilters = signal<FilterOutput[]>([]);
  protected readonly selectedCampaignId = computed(() => {
    const fixedCampaign = this.fixedFilters()?.find(
      (filter) => filter.type === FilterType.Campaign,
    );

    if (fixedCampaign) {
      return fixedCampaign.value?.id as number;
    }

    const selectedCampaign = this.activeFilters().find(
      (filter) => filter.type === FilterType.Campaign,
    );

    return selectedCampaign
      ? (selectedCampaign?.value?.id as number)
      : undefined;
  });

  public ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const filtersFromUrl = this.getFiltersFromUrl(params);

      const areThereNewFilters = filtersFromUrl
        .filter((option) => !!option.value)
        .some(
          (option) =>
            !this.activeFilters().some((filter) => {
              if (filter.type === option.type) {
                const values = option.value.split(",");
                const isDate = filter.type === FilterType.TimePeriod;
                const value = isDate
                  ? (filter.value!.id as Date).getTime()
                  : filter.value?.id;

                return values.includes(`${value}`);
              }
            }),
        );

      if (areThereNewFilters || !this.activeFilters().length) {
        this.getFilterOptions(filtersFromUrl).then((filters) => {
          this.updateActiveFilters(filters);
        });
      }
    });
  }

  protected onActionToggleFilters(): void {
    this.areFiltersOpen.update((areFiltersOpen) => !areFiltersOpen);
  }

  protected onActionOptionSelected(filters: FilterOutput[]): void {
    // if the new filter is a time period, the previous period will be replaced
    if (filters.length && filters[0].type === FilterType.TimePeriod) {
      this.activeFilters.update((fs) =>
        fs.filter((filter) => filter.type !== FilterType.TimePeriod),
      );
    }

    // We just move on if the filters does not exist yet
    if (
      filters.some((filter) =>
        this.activeFilters().some(
          (f) => f.value?.id === filter.value?.id && f.type === filter.type,
        ),
      )
    ) {
      return;
    }

    this.updateActiveFilters([...this.activeFilters(), ...filters]);
  }

  private updateActiveFilters(options: FilterOutput[]): void {
    this.activeFilters.set(options);
    this.filtersChanged.emit(options);
    this.updateUrlParams();
  }

  protected onActionOptionRemoved(filter: FilterOutput): void {
    this.updateActiveFilters(
      this.activeFilters().filter((activeFilter) => activeFilter !== filter),
    );
  }

  public reset(): void {
    this.updateActiveFilters([]);
  }

  private updateUrlParams(): void {
    const currentParams = { ...this.route.snapshot.queryParams };

    // Initialize queryParams with all filter types as null
    const queryParams = Object.values(FilterType).reduce(
      (params, filterType) => {
        // Skip PublicationStatus as it is not a filter from this component
        if (filterType === FilterType.PublicationStatus) {
          return params;
        }
        return { ...params, [filterType]: null };
      },
      {} as Record<string, string | null>,
    );

    // Add the existing filters
    Object.keys(currentParams).forEach((key) => {
      if (!(key in queryParams)) {
        queryParams[key] = currentParams[key];
      }
    });

    // Iterate over active filters and populate queryParams
    this.activeFilters().forEach(({ type, value }) => {
      if (value?.id === undefined) {
        return;
      }

      const id = (
        value.id instanceof Date ? value.id.getTime() : value.id
      ).toString();

      queryParams[type] = queryParams[type] ? `${queryParams[type]},${id}` : id;
    });

    let needsRefresh = false;
    Object.keys(queryParams).forEach((key) => {
      if (queryParams[key] !== currentParams[key]) {
        needsRefresh = true;
      }
    });

    if (needsRefresh) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: "replace",
    });
  }

  private getFiltersFromUrl(params: Params): UrlFilterOption[] {
    const filters: UrlFilterOption[] = [];

    Object.keys(params).forEach((paramKey) => {
      if (Object.values(FilterType).includes(paramKey as FilterType)) {
        filters.push({
          value: params[paramKey],
          type: paramKey as FilterType,
        });
      }
    });

    return filters;
  }

  private async getFilterOptions(
    options: UrlFilterOption[],
  ): Promise<FilterOutput[]> {
    const promises = this.filterTypes().map(async (filterType, index) => {
      // check if we need to call our api to get the filter's name

      const currentFilter = options.find(
        (option) => option.type === filterType,
      );
      if (!currentFilter) {
        return;
      }

      // we get the ids for each filter type
      const ids = options
        .filter((opt) => opt.type === filterType)
        .map((option) => option.value.split(","))
        .flat();

      const selectorOptions =
        await this.filterInteractorsByIds()[index].execute(ids);

      return selectorOptions.entries.map((option) => ({
        type: filterType,
        value: option,
      }));
    });

    const results = await Promise.all(promises);
    return results.filter((res) => !!res).flat();
  }
}
